import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { UILog } from "@dto/log.dto";
import { DateUtil } from "@dto/util/DateUtil";
import { parseJSON } from "@dto/util/util";
import axios, { AxiosError, HttpStatusCode } from "axios";
import { ReactNode } from "react";
import { useAppGlobStore } from "src/appGlobalStore";
import { API_URL } from "src/data/Api";
import { ColorUtil } from "src/util/ColorUtil";
import { GLOB } from "src/util/Glob";
import { RingBuffer } from "src/util/ring-buffer";
import { TextUtil } from "src/util/TextUtil";
import { postApi } from "src/util/apiCalls";

type ErrorDetail = Error | string | object;

// ANSI escape codes are the only coloring the LazyLog viewer understands
const red = (s: unknown) => `\x1b[31m${String(s)}\x1b[0m`;

/**
 * Static methods for logging
 */
export abstract class Log {

  private static readonly STORAGE_KEY = 'uiErrors';
  private static readonly ERROR_BUFFER_SIZE = 19;
  /** The same warning repeated within this window is shown once. */
  private static readonly WARN_DEDUP_MS = 1000;

  private static readonly lastWarn: { timestamp: number, msg: ReactNode } = { msg: '', timestamp: 0 };

  private static counter = 0;

  /** Errors already shown, so a caller and the axios interceptor fallback don't both notify the same one. */
  private static readonly notifiedErrors = new WeakSet<object>();

  static readonly ERRORS = Log.initErrorBuffer();
  static readonly STATIC_NOTIFICATION_KEY = 'staticNotificationKey';

  private static initErrorBuffer() {
    const rb = new RingBuffer(Log.ERROR_BUFFER_SIZE);
    const storedJson = sessionStorage.getItem(Log.STORAGE_KEY);
    if (storedJson) {
      rb.fromArray(parseJSON(storedJson, []));
    }
    return rb;
  }

  /**
   * Notifies the user with a success message, duration 2s
   * @param message ReactNode to display
   */
  static success(message: ReactNode) {
    GLOB.app.notification.success({
      title: "Success",
      duration: 2,
      description: message,
      className: "success-notification",
      style: {
        color: ColorUtil.STATUS_COLOR.pass,
      },
    });
  }

  private static isForbidden(detail: ErrorDetail) {
    return axios.isAxiosError(detail) && detail.response?.status === HttpStatusCode.Forbidden;
  }

  /**
   * Do NOT use directly, sends error to log on server
   * @param log obj to log
   */
  static sendToBE(log: UILog) {
    postApi<BasicResponseDTO, UILog>(API_URL.LOG_ERR, log).catch((reason: AxiosError) => console.error('Failed to store log on server!', reason, log));
  }

  private static addInfo(detail: object) {
    const be = useAppGlobStore.getState().info?.backend;
    return {
      ...detail,
      xormon: be?.version,
      date: DateUtil.getDateISO(),
    };
  }

  private static stringifyDetail(detail: ErrorDetail): string {
    if (!detail) return '';
    if (typeof detail === 'string') return detail;
    if (axios.isAxiosError(detail) && detail.response) return TextUtil.formatJSON(Log.addInfo(detail.response));
    // Error properties are non-enumerable; the own-names list makes them stringify
    if (detail instanceof TypeError) return TextUtil.formatJSON(Log.addInfo(detail), Object.getOwnPropertyNames(detail));
    return TextUtil.formatJSON(Log.addInfo(detail));
  }

  /**
   * Notifies the user with an error message
   * @param message ReactNode to display
   * @param detail object to show in modal on click
   */
  static error(message: ReactNode, detail?: ErrorDetail) {
    // while the backend is down every start-up request fails; the unavailable page already says so
    if (!GLOB.authService.isLoggedIn() || GLOB.authService.startupError || axios.isCancel(detail)) return;

    if (axios.isAxiosError(detail)) {
      if (Log.notifiedErrors.has(detail)) return;
      Log.notifiedErrors.add(detail);
    }

    const detailString = Log.stringifyDetail(detail);

    Log.ERRORS.add(`${red('[Error] ')}${DateUtil.getDateISO()} ${red(message)}${detailString}`);
    sessionStorage.setItem(Log.STORAGE_KEY, TextUtil.formatJSON(Log.ERRORS.toArray()));

    const key = String(Log.counter++);

    GLOB.app.notification.error({
      key,
      title: (Log.isForbidden(detail) ? 'Forbidden' : 'Error') + (detail ? ' (click for detail)' : ''),
      duration: 5,
      description: message,
      className: "error-notification",
      style: {
        cursor: detail ? 'pointer' : 'auto'
      },
      onClick: detail ? () => {
        GLOB.app.notification.destroy(key);
        GLOB.app.modal.info({
          title: 'Error detail',
          icon: null,
          centered: true,
          width: '80%',
          closable: true,
          mask: { closable: true },
          className: 'xm-notification-detail-modal',
          okButtonProps: { style: { display: 'none' } },
          content: <div className="xm-notification-detail">{message}<pre>{detailString}</pre></div>
        });
      } : undefined,
    });
  }

  /**
   * Notifies the user with an info message
   * @param message ReactNode to display
   * @param duration default 3s
   */
  static info(message: ReactNode, duration = 3) {
    GLOB.app.notification.info({
      title: "Info",
      duration,
      description: message,
      className: "info-notification",
    });
  }

  /**
   * Notifies the user with a warning message, but only if the same message was not shown in the last second
   * @param message ReactNode to display
   * @param duration default 4s, 0 = infinite
   */
  static warn(message: ReactNode, duration = 4) {
    if (Log.lastWarn.timestamp + Log.WARN_DEDUP_MS > Date.now() && Log.lastWarn.msg == message) return;
    Log.lastWarn.timestamp = Date.now();
    Log.lastWarn.msg = message;
    GLOB.app.notification.warning({
      key: duration === 0 ? Log.STATIC_NOTIFICATION_KEY : undefined,
      title: "Warning",
      duration,
      description: message,
      className: "warning-notification",
    });
  }

  /**
   * Relays to {@link console.log} if not production, {@link console.debug} otherwise
   * @param params
   */
  static debug(...params: unknown[]) {
    if (GLOB.isProdBuild() && GLOB.isProdEnv()) {
      console.debug(...params);
      return;
    }
    console.log(...params);
  }
}

const toColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 100%, 65%)`;
};

// undefined = off, [] = all, [type1, type2] = only selected; add categories to ConsoleTagType for autocomplete
const ALLOWED_TAG_TYPES: (ConsoleTagType | (string & {}))[] | undefined = undefined;

console.tag = (type, ...msg) => {
  // a trailing boolean argument toggles the single message
  const printAllowed = typeof msg[msg.length - 1] === 'boolean' ? msg.pop() as boolean : true;

  if (!ALLOWED_TAG_TYPES || !printAllowed || (ALLOWED_TAG_TYPES.length && !ALLOWED_TAG_TYPES.includes(type)))
    return;

  console.debug(`%c[${type}]%c`, `color: ${toColor(type)};`, "", ...msg);
};
